import React, { useState } from 'react';
import { BookOpen } from 'lucide-react';
import type { Article } from '../utils/types';
import { useFontSizes } from '../contexts/FontSizeContext';
import { safeUrl, openInWorkingTab } from '../utils/navigate';

interface ArticleCardProps {
  article: Article;
}

/** Max category/tag chips to show on an article tile. */
const MAX_CATEGORY_TAGS = 2;

export const ArticleCard = React.memo<ArticleCardProps>(function ArticleCard({ article }) {
  const { body, small } = useFontSizes();
  const [imgFailed, setImgFailed] = useState(false);
  const showImg = article.imageUrl && !imgFailed;

  // The feed packs every tag into a single comma-separated `category` string
  // (e.g. "Malagouzia, Greek grape varieties, wine"). Show only the first few
  // so the tile stays clean instead of rendering the whole tag list.
  const categoryTags = (article.category ?? '')
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean)
    .slice(0, MAX_CATEGORY_TAGS);

  return (
    <a
      href={safeUrl(article.url)}
      onClick={(e) => openInWorkingTab(article.url, e)}
      className={[
        'botilia-card group flex gap-3 p-3',
        'focus:outline-none focus:ring-2 focus:ring-brand/30 focus:ring-offset-2',
      ].join(' ')}
      aria-label={`${article.title}`}
    >
      {/* Thumbnail */}
      <div className="flex-shrink-0 w-20 h-20 overflow-hidden bg-gray-100">
        {showImg ? (
          <img
            src={article.imageUrl}
            alt=""
            loading="lazy"
            referrerPolicy="no-referrer"
            className="w-full h-full object-cover"
            onError={() => setImgFailed(true)}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-300">
            <BookOpen className="h-8 w-8" />
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        {/* Category tags (capped — feed joins all tags into one string) */}
        {categoryTags.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-1">
            {categoryTags.map((tag) => (
              <span
                key={tag}
                className="inline-block text-[10px] font-semibold uppercase tracking-wider text-brand-ink"
              >
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* Title */}
        <h3 className={`${body} font-semibold text-gray-900 leading-tight line-clamp-1`}>
          {article.title}
        </h3>

        {/* Excerpt */}
        <p className={`${small} text-gray-500 mt-0.5 line-clamp-2 leading-relaxed`}>
          {article.excerpt}
        </p>
      </div>
    </a>
  );
});
